import { Button, Loader, RichTextEditor } from "@/components/ui";
import { Paragraph } from "@/components/ui/Typography";
import { useState } from "react";
import { useMicroSiteContext } from "@/contexts/MicroSiteContext";
import Input from '@/components/ui/Input/Input';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FileUpload from '@/components/ui/FileUpload';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import UploadIcon from "@/assets/icons/UploadIcon";
import { showToast } from '@/lib/toast';
import { TemplateDataSchemaType } from "@/pages/articles-pages/programmes/MultiStepForm/MicroSite/Templates/templateDataSchema";


const FormDataSchema = z.object({
  newsFeedSectionTitle: z.string().min(1, 'Title is required'),
  newsFeedSectionDescription: z.string().min(1, 'Description is required'),
  newsFeedSectionImageUrl: z.string().optional(),
  newsFeedSectionLink: z.string().or(z.url()).optional()
});

const NewsFeedItemForm = ({setShowForm, setIsEditing, newsFeedData}:{setShowForm?: (value: boolean) => void; setIsEditing?: (value: boolean) => void; newsFeedData?: TemplateDataSchemaType["newsFeedSection"]["newsFeedSectionDetails"][0]}) => {
    const [isUploading, setIsUploading] = useState(false);
    const { templatePayload:templateData, updateTemplatePayload } = useMicroSiteContext();

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
      const fileUploadMutation = useFileUpload();
  
  const {
          control,
          handleSubmit,
          setValue,
          formState: { errors },
          reset
        } = useForm({
          resolver: zodResolver(FormDataSchema),
          defaultValues: {
            newsFeedSectionTitle: newsFeedData?.newsFeedSectionTitle ?? '',
          newsFeedSectionDescription: newsFeedData?.newsFeedSectionDescription ?? '',
          newsFeedSectionImageUrl: newsFeedData?.newsFeedSectionImageUrl ?? '',
          newsFeedSectionLink: newsFeedData?.newsFeedSectionLink ?? '',
          },
          mode: 'all'
        });
    
      const handleSave = (formData: TemplateDataSchemaType["newsFeedSection"]["newsFeedSectionDetails"][0]) => {
        setShowForm?.(false);
        setIsEditing?.(false);
        const updatedPayload = {
          ...templateData,
          newsFeedSection: {
            ...templateData.newsFeedSection,
            newsFeedSectionDetails: newsFeedData ? templateData.newsFeedSection.newsFeedSectionDetails.map((item) =>
            item === newsFeedData ? { ...item, newsFeedSectionTitle: formData.newsFeedSectionTitle, newsFeedSectionDescription: formData.newsFeedSectionDescription, newsFeedSectionImageUrl: formData.newsFeedSectionImageUrl, newsFeedSectionLink: formData.newsFeedSectionLink } : item
          ) : templateData.newsFeedSection.newsFeedSectionDetails.concat([formData])
          }
        };
        updateTemplatePayload(updatedPayload);
      };
  
          const handleFileChange = async(files: File[] | null) => {
          if (files && files.length > 0) {
            setIsUploading(true);
            const file = files[0];
            try {
              const response = await fileUploadMutation.mutateAsync({
                file: file
              });
              setValue('newsFeedSectionImageUrl', response?.data?.fileUrl, { shouldValidate: true });
              setPreviewUrl(response?.data?.fileUrl);
            } catch (error) {
              console.error('File upload failed:', error);
              showToast.error('File upload failed. Please try again.');
            } finally {
              setIsUploading(false);
            }
          } else {
            setPreviewUrl(null);
            setValue('newsFeedSectionImageUrl', "", { shouldValidate: true });
          }
        };

    return (
      <div className="flex flex-col gap-4 w-full p-4 lg:p-8 bg-[#F2F2F2]">
        <form onSubmit={handleSubmit(handleSave)} className="flex flex-col gap-2">
        
         <Controller
          name="newsFeedSectionTitle"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Title"
              label="Title"
              error={errors.newsFeedSectionTitle?.message}
              $dataId="title-input"
              required
              
            />
          )}
        />

           <Controller
          name="newsFeedSectionDescription"
          control={control}
          render={({ field }) => (
            <RichTextEditor
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Description"
              label="Description"
              error={errors.newsFeedSectionDescription?.message}
              required
            />
          )}
        />

         <Controller
          name="newsFeedSectionLink"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Link"
              label="Link (Optional)"
              error={errors.newsFeedSectionLink?.message}
              $dataId="newsFeedSectionLink-input"
              
            />
          )}
        />

           <div className="max-h-64 overflow-hidden flex items-center justify-center">
          {
            (previewUrl || newsFeedData?.newsFeedSectionImageUrl) && !isUploading ? (
              <img src={previewUrl || newsFeedData?.newsFeedSectionImageUrl} alt="Preview" className="h-full w-full object-cover object-center rounded" />
            ) : null
          }
            
          </div>

          <div className="relative">
              {
              isUploading && <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                <Loader />  
              </div> 
            }
            <Controller
          name="newsFeedSectionImageUrl"
          control={control}
          render={({ field }) => (
            <FileUpload
            {...field}
              onChange={(files) => {
                handleFileChange(files);
                field.onChange(files ? URL.createObjectURL(files[0]) : '');
              }}
              label=""
              placeholder=""
              $maxSizeMB={5}
              error={errors.newsFeedSectionImageUrl?.message}
              multiple={false}
              allowedFileTypes={['image/*']}
              containerClassName="border border-[#E5E5E5] bg-white"
              $dataId="newsFeedSectionImageUrl-file-upload"
            >
              <div className="flex flex-col items-center justify-center gap-4">
                <UploadIcon stroke="#00764B" />
                <Paragraph className="font-semibold text-primary">
                  Click or drag and drop file to upload
                </Paragraph>
                <Paragraph className="text-sm text-text-secondary">
                  Supported document file is PNG, & JPEG with max size of 5mb
                </Paragraph>
                <button
                  type="button"
                  className="bg-[#ECFEF8] text-[#00764B] hover:bg-[#ECFEF8] hover:text-[#000] px-4 py-2 rounded-md font-semibold"
                >
                  Browse file
                </button>
              </div>
            </FileUpload>
          )}
        />
          </div>

        
    

          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="secondary"
              className=""
              onClick={() => {
                setShowForm?.(false);
                setIsEditing?.(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button
              className=""
              onClick={handleSubmit(handleSave)}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    )
  };

  export default NewsFeedItemForm;